// src/lib/auth.js
import history from "./history";
import { logout } from "../reducers/authReducer";
import { parseToken } from "./helpers";
import { runWithAdal } from "react-adal";
import { authContext } from "../lib/config";
import axios from "axios";
import moment from "moment-timezone";
import {
  isMsalMode,
  startServerLogin,
  redeemPreviewToken,
  applyServerSession,
  applyAuthResponseSession,
} from "./authMode";
import { clearAllReportSessionState } from "../components/powerBI/components/powerbi-report/reportSessionState";
import { clearTokenCache } from "../components/powerBI/components/report/reducers/reportReducer";
import { clearPbiEmbedPool } from "../components/powerBI/components/powerbi-report/pbiEmbedPool";

// Configuration constants
const IDLE_TIMEOUT_MINUTES =
  Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES) || 60; // Adjust based on sensitivity
const ABSOLUTE_TIMEOUT_HOURS =
  Number(process.env.REACT_APP_ABSOLUTE_TIMEOUT_HOURS) || 8; // Maximum session duration
const ACTIVITY_CHECK_INTERVAL = 60000; // Check every minute
const IDLE_WARNING_MINUTES_BEFORE =
  Number(process.env.REACT_APP_IDLE_WARNING_MINUTES_BEFORE) || 2; // Warn this many minutes before idle logout

class Auth {
  constructor(store) {
    this.store = store;
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.updateActivity = this.updateActivity.bind(this);
    this.checkIdleTimeout = this.checkIdleTimeout.bind(this);
    this.handleSSOAuth = this.handleSSOAuth.bind(this);
    this.handleSetClient = this.handleSetClient.bind(this);
    this.handleStaleSession = this.handleStaleSession.bind(this);

    window.AuthenticationContext = authContext;
    window.authContext = authContext;

    // Initialize activity tracking (only for non-SSO sessions)
    this.activityTrackingInitialized = false;

    // UI hooks: set by a mounted component (see Profile) to show/hide the
    // idle session-expiry warning dialog.
    this.onIdleWarning = null;
    this.onIdleWarningDismiss = null;
    this.idleWarningShown = false;
  }

  /**
   * Initialize activity tracking listeners
   */
  initActivityTracking() {
    if (this.activityTrackingInitialized) return;

    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];

    events.forEach((event) => {
      document.addEventListener(event, this.updateActivity, true);
    });

    // Start idle timeout checker
    this.idleCheckInterval = setInterval(this.checkIdleTimeout, ACTIVITY_CHECK_INTERVAL);

    this.activityTrackingInitialized = true;
    // Login paths already stamp last_activity; don't let a page reload reset the idle clock.
    if (!localStorage.getItem("last_activity")) {
      localStorage.setItem("last_activity", moment().valueOf());
    }
    // console.log("Activity tracking initialized");
  }

  /**
   * Restart idle tracking for an existing session on page load - tracking
   * started at login doesn't survive a reload/new tab.
   */
  resumeActivityTracking() {
    // /callback runs its own handleAuthentication/handleSSOAuth, which starts tracking.
    if (!this.hasSession() || history.location.pathname.includes("/callback")) return;
    this.initActivityTracking();
    this.checkIdleTimeout();
  }

  /**
   * Update last activity timestamp
   */
  updateActivity(event) {
    // Presses inside the warning dialog must reach its buttons; they aren't "activity".
    if (event?.target?.closest?.(".session-timeout-dialog")) return;

    // Only update if we have a session (lightweight check)
    if (this.hasSession()) {
      const now = moment().valueOf();
      localStorage.setItem("last_activity", now);

      // Any activity clears a pending idle warning immediately, rather than
      // waiting for the next once-a-minute checkIdleTimeout tick.
      this.dismissIdleWarning();
    }
  }

  /**
   * Whether this browser has a signed-in session. In msal mode there is no
   * token to look for (the session cookie is HttpOnly), so the non-secret
   * expiry mirrored from the server stands in for it.
   */
  hasSession() {
    return isMsalMode() ? Boolean(localStorage.getItem("expires_at")) : Boolean(localStorage.getItem("access_token"));
  }

  /**
   * Hide the idle warning dialog and cancel the deadline logout timer.
   */
  dismissIdleWarning() {
    clearTimeout(this.idleLogoutTimeout);
    this.idleLogoutTimeout = null;
    if (!this.idleWarningShown) return;
    this.idleWarningShown = false;
    if (this.onIdleWarningDismiss) this.onIdleWarningDismiss();
  }

  /**
   * Check if user has been idle too long
   */
  checkIdleTimeout() {
    // Skip timeout checks for upload/file share routes
    if (history.location.pathname.includes("/uploadFile") || history.location.pathname.includes("/tabsFileShare")) {
      return;
    }

    const lastActivity = localStorage.getItem("last_activity");
    const expiresAt = localStorage.getItem("expires_at");
    const sessionStart = localStorage.getItem("session_start");
    const sso = localStorage.getItem("sso");

    if (!lastActivity || !expiresAt) return;

    const now = moment();
    const lastActivityTime = moment(parseInt(lastActivity, 10));
    const sessionStartTime = moment(parseInt(sessionStart, 10));
    const tokenExpiry = moment(parseInt(expiresAt, 10));

    // Idle and absolute timeout apply to every session, SSO included - a
    // captured/left-open SSO session must expire the same as any other.
    // Only the ADAL token-renewal step below is specific to non-SSO logins.
    const idleMinutes = now.diff(lastActivityTime, "minutes");
    // console.log(`Idle check: ${idleMinutes} minutes since last activity`);
    if (idleMinutes >= IDLE_TIMEOUT_MINUTES) {
      console.warn("Session expired due to inactivity");
      this.dismissIdleWarning();
      this.logout("idle", { fullLogout: false });
      return;
    }

    // Warn shortly before the idle cutoff so the user has a chance to stay
    // signed in instead of silently losing in-progress work.
    if (idleMinutes >= IDLE_TIMEOUT_MINUTES - IDLE_WARNING_MINUTES_BEFORE) {
      const idleDeadline = lastActivityTime.valueOf() + IDLE_TIMEOUT_MINUTES * 60000;
      // Re-check at the deadline so logout lines up with the dialog countdown, not the next minute tick.
      clearTimeout(this.idleLogoutTimeout);
      this.idleLogoutTimeout = setTimeout(this.checkIdleTimeout, idleDeadline - now.valueOf());
      if (!this.idleWarningShown && this.onIdleWarning) {
        this.idleWarningShown = true;
        this.onIdleWarning(idleDeadline);
      }
    } else {
      this.dismissIdleWarning();
    }

    // Check absolute timeout
    if (sessionStart) {
      const sessionHours = now.diff(sessionStartTime, "hours");
      if (sessionHours >= ABSOLUTE_TIMEOUT_HOURS) {
        console.warn("Session expired due to maximum duration");
        this.logout("absolute", { fullLogout: false });
        return;
      }
    }

    // Only renew token if user is active and within limits (ADAL/non-SSO only;
    // a server-side session has nothing in the browser to renew)
    if (sso === "false" && !isMsalMode()) {
      const minutesUntilExpiry = tokenExpiry.diff(now, "minutes");
      if (minutesUntilExpiry <= 5 && minutesUntilExpiry > 0) {
        this.renewToken();
      }
    }
  }

  /**
   * Renew authentication token
   */
  renewToken() {
    if (isMsalMode()) return;
    window.authContext.acquireToken(authContext.config.clientId, (error, token) => {
      if (error) {
        console.error("Token renewal failed:", error);
        return;
      }

      if (token) {
        const parsedToken = parseToken(token);
        const expiresAt = parsedToken.exp * 1000;

        localStorage.setItem("access_token", token);
        localStorage.setItem("expires_at", expiresAt);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        console.log("Token renewed successfully");
      }
    });
  }

  login(username, type) {
    if (isMsalMode() && type !== "saml") {
      // The server runs the Azure sign-in and sets the session cookie; this
      // page is about to be replaced by the redirect to Azure.
      const deepLink = window.location.pathname + window.location.search;
      startServerLogin({
        returnTo: deepLink && deepLink !== "/" && !deepLink.includes("/callback") ? deepLink : "/",
        loginHint: username,
      });
      return;
    }
    sessionStorage.setItem("logging_in", "true");
    // Reset the stale-session guard so a fresh sign-in can surface a future
    // STALE_SESSION response (the guard is set in handleStaleSession).
    sessionStorage.removeItem("stale_session_handling");
    if (type === "saml") {
      var element = window.document.createElement("a");
      element.setAttribute("href", process.env.REACT_APP_BIGHORN_SERVER + "/api/samlLogin?" + username.split("@")[1]);
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } else {
      window.authContext.handleWindowCallback();
      let access = localStorage.getItem("access_token");

      if (!window.authContext.isCallback(window.location.hash)) {
        if (access === null) {
          // No token, proceed with login
        } else {
          window.authContext.acquireToken(authContext.config.clientId, (error, token) => {
            if (token !== null) {
              axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
              localStorage.setItem("access_token", token);
            } else {
              axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("access_token")}`;
            }
          });
        }
      }

      authContext.config.extraQueryParameter = `login_hint=${username}`;
      runWithAdal(authContext, () => {
        sessionStorage.removeItem("logging_in");
        localStorage.setItem("last_activity", moment().valueOf());
        // Initialize activity tracking after successful login
        this.initActivityTracking();
      });
    }
  }

  handleSSOAuth() {
    localStorage.setItem("sso", "true");
    let parsedToken = parseToken(window.location.hash);
    localStorage.setItem("user", parsedToken.id);
    localStorage.setItem("client", parsedToken.client);
    this.setSession(window.location.hash.split("=")[1], true);
    const now = moment().valueOf();
    localStorage.setItem("session_start", now);
    localStorage.setItem("last_activity", now);
    localStorage.setItem("expires_at", moment().add(24, "hours").format("x"));
    sessionStorage.removeItem("logging_in");
    this.initActivityTracking();
  }

  handleAuthentication() {
    localStorage.setItem("sso", "false");
    var requestInfo = authContext.getRequestInfo(window.location.hash);
    if (requestInfo.valid) {
      authContext.saveTokenFromHash(requestInfo);
      sessionStorage.removeItem("logging_in");
      if (requestInfo.parameters.id_token) {
        this.setSession(requestInfo.parameters.id_token, false);
        // Initialize activity tracking after authentication
        if (this.initActivityTracking) {
          this.initActivityTracking();
        }
      }
    } else {
      // Clear flag even if authentication failed
      sessionStorage.removeItem("logging_in");
    }
  }

  /**
   * msal mode: exchange the one-time preview token from the URL fragment for a
   * server-side cookie session. Resolves to true when the session is ready.
   */
  async handleSetClientWithCookie() {
    const hashToken = window.location.hash.replace("#", "");
    // Take the one-time token out of the address bar and history right away.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (!hashToken) {
      console.error("No token found in hash");
      sessionStorage.removeItem("logging_in");
      return false;
    }
    try {
      const session = await redeemPreviewToken(hashToken);
      applyAuthResponseSession(session);
      localStorage.setItem("last_activity", moment().valueOf());
      sessionStorage.removeItem("logging_in");
      this.initActivityTracking();
      return true;
    } catch (err) {
      console.error("Error opening the client preview:", err);
      sessionStorage.removeItem("logging_in");
      return false;
    }
  }

  handleSetClient() {
    if (isMsalMode()) return this.handleSetClientWithCookie();
    localStorage.setItem("sso", "false");

    try {
      const hashToken = window.location.hash.replace("#", "");
      if (!hashToken) {
        console.error("No token found in hash");
        sessionStorage.removeItem("logging_in");
        return;
      }
      localStorage.setItem("setClientToken", hashToken);
      let parsedToken = parseToken(window.location.hash);
      if (!parsedToken.accessToken) {
        console.error("Invalid token structure");
        sessionStorage.removeItem("logging_in");
        this.clearLocalSession();
        return;
      }
      localStorage.setItem("access_token", parsedToken.accessToken);
      localStorage.setItem("expires_at", (parsedToken.iat + 7200) * 1000);
      axios.defaults.headers.common["Authorization"] = `Bearer ${parsedToken.accessToken}`;

      let re = new RegExp(/^live.com#/);
      let oRe = new RegExp(/^mail#/);
      var testUser = parsedToken.user;
      if (re.test(testUser)) {
        testUser = testUser.split("live.com#")[1];
      }
      if (oRe.test(testUser)) {
        testUser = testUser.split("mail#")[1];
      }
      localStorage.setItem("user", testUser);

      const now = moment().valueOf();
      localStorage.setItem("session_start", now);
      localStorage.setItem("last_activity", now);
      sessionStorage.removeItem("logging_in");

      // Initialize activity tracking for setClient auth
      this.initActivityTracking();

      // console.log("SetClient authentication successful");
      // Clear the logging_in flag after a short delay to ensure everything is set
      setTimeout(() => {
        sessionStorage.removeItem("logging_in");
      }, 100);
    } catch (err) {
      console.error("Error in handleSetClient:", err);
      sessionStorage.removeItem("logging_in");
      // Clear any partial auth state
      this.clearLocalSession();
    }
  }

  setSession(hash, isSso = false) {
    let parsedToken = parseToken(hash);
    let expiresAt = parsedToken.exp * 1000;
    const now = moment().valueOf();

    localStorage.setItem("access_token", hash);
    localStorage.setItem("expires_at", expiresAt);
    localStorage.setItem("session_start", now);
    localStorage.setItem("last_activity", now);

    if (parsedToken.unique_name !== undefined) {
      let re = new RegExp(/^live.com#/);
      let oRe = new RegExp(/^mail#/);
      var testUser = parsedToken.unique_name;
      if (re.test(testUser)) {
        testUser = testUser.split("live.com#")[1];
      }
      if (oRe.test(testUser)) {
        testUser = testUser.split("mail#")[1];
      }
      localStorage.setItem("user", testUser);
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${hash}`;
  }

  /**
   * Best-effort call to revoke the current Client Token server-side.
   * Uses fetch+keepalive (not axios) so the request survives the page
   * reload/redirect that immediately follows logout.
   */
  notifyServerLogout() {
    if (!process.env.REACT_APP_BIGHORN_SERVER) return Promise.resolve();

    if (isMsalMode()) {
      // The session lives in an HttpOnly cookie, so there is no token to look
      // for - always tell the server, which revokes it and expires the cookie.
      return fetch(`${process.env.REACT_APP_BIGHORN_SERVER}/api/app/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "bighorn" },
        credentials: "include",
        body: "{}",
        keepalive: true,
      }).catch(() => {
        // Best-effort only - the session still expires at its exp.
      });
    }

    const clientToken = localStorage.getItem("clientToken");
    if (!clientToken) return Promise.resolve();

    return fetch(`${process.env.REACT_APP_BIGHORN_SERVER}/api/app/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ClientToken: clientToken },
      body: "{}",
      keepalive: true,
    }).catch(() => {
      // Best-effort only - the token still expires naturally at its exp.
    });
  }

  // src/lib/auth.js

  // Update the clearLocalSession method
  clearLocalSession() {
    // Set flag to prevent axios interceptor from triggering during logout
    sessionStorage.setItem("logging_out", "true");

    // Dismiss any pending idle-warning dialog - it no longer applies once
    // the session is being torn down (e.g. a manual logout mid-warning).
    this.dismissIdleWarning();

    // Tell the server to revoke this Client Token so a copy captured before
    // logout/idle-timeout can't be replayed for the rest of its ~10h lifetime.
    // Best-effort: fetch+keepalive gives the request a chance to land even
    // though logoutFromApp() reloads the page right after this runs.
    const serverLogout = this.notifyServerLogout();

    // Clear activity tracking
    if (this.idleCheckInterval) {
      clearInterval(this.idleCheckInterval);
      this.idleCheckInterval = null;
    }

    // Remove event listeners
    if (this.activityTrackingInitialized) {
      const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
      events.forEach((event) => {
        document.removeEventListener(event, this.updateActivity, true);
      });
      this.activityTrackingInitialized = false;
    }

    // Clear local storage
    const keysToRemove = [
      "access_token",
      "id_token",
      "expires_at",
      "groupId",
      "client",
      "clientToken",
      "setClientToken",
      "sso",
      "user",
      "last_activity",
      "session_start",
    ];

    keysToRemove.forEach((key) => localStorage.removeItem(key));
    if (isMsalMode()) applyServerSession(null);

    // Clear ADAL session storage
    let i = sessionStorage.length;
    while (i--) {
      const key = sessionStorage.key(i);
      if (/adal/.test(key)) {
        sessionStorage.removeItem(key);
      }
    }
    sessionStorage.removeItem("adal.access.token.keyhttps://graph.microsoft.com");

    // Clear axios authorization header
    delete axios.defaults.headers.common["Authorization"];

    clearAllReportSessionState();
    clearTokenCache();
    clearPbiEmbedPool();

    return serverLogout;
  }

  // msal mode: the session cookie is only cleared once the server answers, and
  // the next page load asks the server who is signed in - so wait for the
  // logout (bounded) before reloading or redirecting away.
  afterServerLogout(serverLogout, next) {
    Promise.race([serverLogout, new Promise((resolve) => setTimeout(resolve, 3000))]).then(next);
  }

  /**
   * Application-only logout
   * Clears local tokens but keeps Microsoft session active
   */
  logoutFromApp() {
    // console.log("Logging out from application only");
    const serverLogout = this.clearLocalSession();
    this.store.dispatch(logout(true));

    // Clear ADAL cache but don't redirect to Microsoft
    authContext.clearCache();

    // Clear the logging out flag after a short delay
    setTimeout(() => {
      sessionStorage.removeItem("logging_out");
    }, 100);

    // Make sure we redirect to home and force re-render
    const finish = () => {
      history.replace("/");
      window.location.reload(); // Force a full page reload to clear any cached state
    };
    if (isMsalMode()) {
      this.afterServerLogout(serverLogout, finish);
      return;
    }
    finish();
  }

  /**
   * Full Microsoft logout
   * Redirects to Microsoft logout page and clears everything
   */
  logoutFromMicrosoft(promptOnNextLogin = true) {
    // console.log("Logging out from Microsoft");
    const serverLogout = this.clearLocalSession();
    this.store.dispatch(logout(true));

    const tenant = authContext.config.tenant || "common";
    const postLogoutRedirectUri = encodeURIComponent(authContext.config.postLogoutRedirectUri || window.location.origin);

    // Build logout URL
    let logoutUrl = `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutRedirectUri}`;

    // Add prompt parameter to force account selection on next login
    if (promptOnNextLogin) {
      logoutUrl += "&prompt=select_account";
    }

    // Note: We don't clear the logging_out flag here because we're redirecting away
    // Redirect to Microsoft logout
    if (isMsalMode()) {
      this.afterServerLogout(serverLogout, () => {
        window.location.href = logoutUrl;
      });
      return;
    }
    window.location.href = logoutUrl;
  }

  /**
   * Handle a stale/deleted directory session.
   *
   * The server (BIG-1663) returns 401 { code: "STALE_SESSION" } when the browser
   * presents an Azure AD token for an oid that no longer exists (e.g. the account
   * was deleted and re-invited). The AAD token itself is invalid, so an app-only
   * logout would immediately re-acquire the same stale token and loop. We must do
   * a full Microsoft logout with account selection so the user re-authenticates
   * against the current directory object.
   *
   * @param {string} message - Remediation message from the server (optional).
   */
  handleStaleSession(message) {
    // Guard against re-entrancy: several in-flight requests can 401 at once, and
    // we only want one alert + one redirect. Reset on the next login().
    if (sessionStorage.getItem("stale_session_handling") === "true") {
      return;
    }
    sessionStorage.setItem("stale_session_handling", "true");

    const remediation =
      message ||
      "Your sign-in session is out of date and no longer matches a valid account. " +
        "Please sign out of your Microsoft account completely (not just clear your browser cache) and sign back in.";

    // Blocking on purpose: the user needs to read the remediation before we
    // redirect to the Microsoft logout page (a toast would not survive the redirect).
    window.alert(remediation);

    // Force a full Microsoft logout (not app-only) with the account picker so the
    // stale AAD token is cleared and the user can pick the correct account.
    this.logoutFromMicrosoft(true);
  }

  /**
   * Main logout method with options
   * @param {string} reason - Reason for logout (user, idle, absolute, unauthorized)
   * @param {object} options - Logout options
   */
  logout(reason = "user", options = {}) {
    const {
      fullLogout = true, // Full Microsoft logout vs app-only
      promptOnNextLogin = true, // Show account picker on next login
      showDialog = false, // Show logout options dialog
    } = options;

    // console.log(`Logout initiated: ${reason}`, options);

    const sso = localStorage.getItem("sso");

    // For SSO sessions, always do app-only logout
    if (sso === "true") {
      this.logoutFromApp();
      return;
    }

    // For automatic logouts (idle, absolute, unauthorized), do app-only
    if (reason !== "user") {
      this.logoutFromApp();
      return;
    }

    // For user-initiated logout, allow choice
    if (showDialog) {
      // This will be handled by the component
      if (this.onShowLogoutDialog) {
        this.onShowLogoutDialog();
      }
      return;
    }

    // Default behavior based on fullLogout option
    if (fullLogout) {
      this.logoutFromMicrosoft(promptOnNextLogin);
    } else {
      this.logoutFromApp();
    }
  }

  isAuthenticated() {
    let sso = localStorage.getItem("sso");
    let expiresAt = localStorage.getItem("expires_at");

    // Special routes always authenticated
    if (history.location.pathname.includes("/uploadFile") || history.location.pathname.includes("/tabsFileShare")) {
      return true;
    }

    if (isMsalMode()) {
      // A server-side session has a fixed lifetime and nothing in the browser
      // to renew; reaching its expiry ends it (the idle check logs out first).
      return Boolean(expiresAt) && new Date().getTime() < expiresAt;
    }

    if (sso === "false") {
      const tokenExpiry = moment(parseInt(expiresAt, 10));
      const now = moment();

      // Check if token is expired
      if (now >= tokenExpiry) {
        return false;
      }

      // Token renewal logic (don't check idle timeout here!)
      const newDate = moment(tokenExpiry).subtract(25, "minutes");
      if (now > newDate && now < tokenExpiry) {
        window.authContext.acquireToken(authContext.config.clientId, (error, token) => {
          if (token !== null) {
            axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
            localStorage.setItem("access_token", token);
            const parsedToken = parseToken(token);
            localStorage.setItem("expires_at", parsedToken.exp * 1000);
          } else {
            axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("access_token")}`;
          }
        });
      }

      return now < tokenExpiry;
    } else {
      // SSO session
      return new Date().getTime() < expiresAt;
    }
  }

  /**
   * Cleanup method to remove event listeners
   */
  cleanup() {
    if (this.idleCheckInterval) {
      clearInterval(this.idleCheckInterval);
    }
    clearTimeout(this.idleLogoutTimeout);

    if (this.activityTrackingInitialized) {
      const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
      events.forEach((event) => {
        document.removeEventListener(event, this.updateActivity, true);
      });
      this.activityTrackingInitialized = false;
    }
  }
}

export default Auth;
